import {
  WSAlert,
  WSAvatarProps,
  WSList,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";

import { CollapsibleItem } from "../../../../shared/components/CollapsibleItem/CollapsibleItem";

import { RequirementContextType } from "../../types";
import type { insuranceImportApiSpec } from "@wingspanhq/wingspan-api-types";

type Props = {
  item: insuranceImportApiSpec.InsuranceImportJob;
  contextType: RequirementContextType;
} & WSMarginProps;

export const ValidationItem: React.FC<Props> = ({
  item,
  contextType,
  ...marginProps
}) => {
  const { title, description } = item;

  const status = item.status || "Created";
  const statusReason = item.statusReason || "";

  const avatarProps = useMemo(() => {
    const statusToPropsMap: Record<
      insuranceImportApiSpec.InsuranceImportJob["status"],
      WSAvatarProps
    > = {
      Created: {
        type: "icon",
        colorBackground: "gray300",
        color: "gray600",
        icon: "time"
      },
      Pending: {
        type: "icon",
        colorBackground: "gray300",
        color: "gray600",
        icon: "time"
      },
      Processing: {
        type: "icon",
        colorBackground: "gray50",
        color: "blue300",
        icon: "loader"
      },
      Complete: {
        type: "icon",
        colorBackground: "green400",
        color: "white",
        icon: "check-circle"
      },
      Failed: {
        type: "icon",
        colorBackground: "red400",
        color: "white",
        icon: "alert-circle"
      },
      Error: {
        type: "icon",
        colorBackground: "red400",
        color: "white",
        icon: "alert-circle"
      } // TODO: need to differentiate between Error and Failed
    };

    return statusToPropsMap[status];
  }, [status]);

  // expand the item if status is Failed OR Error OR Complete with warnings
  const expanded = ["Failed", "Failed", "Complete"].includes(status);

  return (
    <CollapsibleItem
      avatarProps={avatarProps}
      title={title}
      isExpanded={expanded}
      {...marginProps}
    >
      <WSList gap="M">
        <WSText.ParagraphSm weight="book" color="gray600">
          {description}
        </WSText.ParagraphSm>

        {status === "Complete" && (
          <WSAlert icon="check-circle" theme="success" title="Passed" />
        )}

        {statusReason && status === "Failed" && (
          <WSAlert icon="alert-circle" theme="error" title="Failed">
            {statusReason}
          </WSAlert>
        )}
      </WSList>
    </CollapsibleItem>
  );
};
